<template>
	<div class="billable-item-nest">
		<template v-if="items.length">
			<div class="bi-row bi-header">
				<div class="bi-cell bi-header__cell">Billable Item</div>
				<div class="bi-cell bi-header__cell">Start</div>
				<div class="bi-cell bi-header__cell">End</div>
				<div class="bi-cell bi-header__cell">Hours</div>
				<div class="bi-cell bi-header__cell">Closed</div>
				<div class="bi-cell bi-header__cell">Billed</div>
				<div class="bi-cell bi-header__cell">Invoiced</div>
				<div class="bi-cell bi-header__cell">Adjustment</div>
				<div class="bi-cell bi-header__cell">Status</div>
				<div class="bi-cell bi-header__cell bi-header__cell--actions">Actions</div>
			</div>

			<div
				v-for="item in items"
				:key="item.id"
				class="bi-row"
				:class="{ 'bi-row--clickable': permissions.detail }"
				@click="permissions.detail ? $q.goTo('/admin/billableItem/' + item.id) : null"
			>
				<div class="bi-cell bi-cell--code">
					<div class="bi-label">Billable Item</div>
					<div class="bi-value font-weight-medium">{{ item.code }}</div>
				</div>
				<div class="bi-cell bi-cell--date">
					<div class="bi-label">Start Date</div>
					<div class="bi-value">{{ item.startDate | formatDate }}</div>
				</div>
				<div class="bi-cell bi-cell--date">
					<div class="bi-label">End Date</div>
					<div class="bi-value">{{ item.endDate | formatDate }}</div>
				</div>
				<div class="bi-cell bi-cell--num">
					<div class="bi-label">Hours</div>
					<div class="bi-value">{{ item.hours | number(2) }}</div>
				</div>
				<div class="bi-cell bi-cell--closed">
					<div class="bi-label">Closed</div>
					<div class="bi-value">
						<check-switch :value="item.isClosed"/>
					</div>
				</div>
				<div class="bi-cell bi-cell--num">
					<div class="bi-label">Billed</div>
					<div class="bi-value">{{ currencyFormat(item.total) }}</div>
				</div>
				<div class="bi-cell bi-cell--num">
					<div class="bi-label">Invoiced</div>
					<div class="bi-value">{{ currencyFormat(item.invoiced) }}</div>
				</div>
				<div class="bi-cell bi-cell--num">
					<div class="bi-label">Adjustment</div>
					<div
						class="bi-value"
						:class="{ 'error--text': item.adjustmentAmount < 0, 'success--text': item.adjustmentAmount > 0 }"
					>
						{{ item.adjustmentAmount ? currencyFormat(item.adjustmentAmount) : 'N/A' }}
					</div>
				</div>
				<div class="bi-cell bi-cell--status">
					<div class="bi-label">Status</div>
					<div class="bi-value">
						<v-menu offset-x right open-on-hover>
							<template #activator="{ on }">
								<v-btn
									v-on="on"
									plain small class="rounded-xl status-chip"
									:color="item.statusColor"
									:style="{ background: item.statusLightColor }"
								>{{ shortStatusLabel(item) }}</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-for="approver in (item.approvers || []).filter(x => x.statusToApprove === 2)"
									:key="approver.id"
								>
									<div
										class="d-flex justify-space-between align-center"
										style="min-width: 20px; width: 100%"
									>
										<span class="mr-2 body-2">
											<user-avatar :user-id="approver.approverId" :size="28" :show-card="false"/>
											{{ approver.approverBlueTag }}
										</span>
										<span :set="status = approverStatus.find(s => s.id == approver.status)">
											<v-chip
												v-if="status"
												:color="status.lightColor"
												:style="{ color: status.color }"
												small
											>
												{{ status.description }}
											</v-chip>
										</span>
									</div>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
				<div v-if="permissions.actions" class="bi-cell bi-cell--actions" @click.stop>
					<div class="bi-label">Actions</div>
					<div class="bi-value bi-actions">
						<div class="bi-actions__slot">
							<v-tooltip v-if="item.canApprove" top z-index="200">
								<template #activator="{ on }">
									<v-btn v-on="on" color="primary" @click="approve(item, true)" icon x-small>
										<v-icon size="20">mdi-check</v-icon>
									</v-btn>
								</template>
								<span class="caption">Approve</span>
							</v-tooltip>
						</div>
						<div class="bi-actions__slot">
							<v-tooltip v-if="item.canApprove" top z-index="200">
								<template #activator="{ on }">
									<v-btn v-on="on" color="primary" @click="approve(item, false)" icon x-small>
										<v-icon size="20">mdi-undo</v-icon>
									</v-btn>
								</template>
								<span class="caption">Send to Draft</span>
							</v-tooltip>
						</div>
						<div class="bi-actions__slot">
							<v-menu left offset-x>
								<template #activator="{ on }">
									<v-btn v-on="on" color="primary" icon x-small>
										<v-icon size="20">mdi-dots-vertical</v-icon>
									</v-btn>
								</template>
								<v-list dense class="py-1">
									<v-list-item class="pa-1 py-0">
										<v-tooltip right z-index="200">
											<template #activator="{ on }">
												<v-btn v-on="on" color="#ff6347" @click="$adminSurf('billableItemToPdf', item)" icon>
													<v-icon>mdi-file-pdf-box</v-icon>
												</v-btn>
											</template>
											<span class="caption">Export to PDF</span>
										</v-tooltip>
									</v-list-item>
									<v-list-item class="pa-1 py-0">
										<v-tooltip right z-index="200">
											<template #activator="{ on }">
												<v-btn v-on="on" @click="$adminSurf('openHistory', item, 'billableItem')" icon>
													<v-icon>mdi-history</v-icon>
												</v-btn>
											</template>
											<span class="caption">History</span>
										</v-tooltip>
									</v-list-item>
									<v-list-item v-if="!item.canApprove" class="pa-1 py-0">
										<v-tooltip right z-index="200">
											<template #activator="{ on }">
												<v-btn v-on="on" @click="deleteBillableItem(item)" icon color="error">
													<v-icon>mdi-cancel</v-icon>
												</v-btn>
											</template>
											<span class="caption">Cancel</span>
										</v-tooltip>
									</v-list-item>
									<v-list-item class="pa-1 py-0" v-if="[3,4].includes(item.status)">
										<v-tooltip right z-index="200">
											<template #activator="{ on }">
												<v-btn v-on="on" @click="createInvoice(item)" icon>
													<v-icon>mdi-script-text-outline</v-icon>
												</v-btn>
											</template>
											<span class="caption">Create Invoice</span>
										</v-tooltip>
									</v-list-item>
									<v-list-item class="pa-1 py-0">
										<watchers-menu
											@watcher-added="item.subscribers.push($event)"
											@watcher-removed="item.subscribers.splice(item.subscribers.findIndex(x => x.userId == $event), 1)"
											:entity-id="item.id"
											:watchers="item.subscribers"
											required-permission="billableItems"
										>
											<template #default="{ on }">
												<v-btn v-on="on" icon>
													<v-icon>mdi-eye</v-icon>
												</v-btn>
											</template>
										</watchers-menu>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</div>
				</div>
			</div>
		</template>

		<div v-else class="billable-item-nest__empty">
			<div class="billable-item-nest__empty-text">No billable items for this billable yet.</div>
			<v-btn
				v-if="permissions.add"
				small
				color="primary"
				class="text-none"
				:to="{ name: 'CreateBillableItem' }"
			>
				Create First Billable Item
			</v-btn>
		</div>

		<billable-item-delete-dialog ref="deleteDialog"/>
	</div>
</template>

<script>
import BillableItemDeleteDialog from '../../../dialogs/adminSurf/BillableItemDeleteDialog'
import WatchersMenu from '../../admin-surf/WatchersMenu.vue'
import CheckSwitch from '../../CheckSwitch.vue'

export default {
	name: 'BillableItemNestPanel',
	components: { BillableItemDeleteDialog, WatchersMenu, CheckSwitch },
	props: {
		items: { type: Array, default: () => [] },
		approverStatus: { type: Array, default: () => [] },
		currentUser: { type: Object, default: () => ({}) },
	},
	data: () => ({
		permissions: {
			detail: false,
			add: false,
			actions: false,
		},
	}),
	mounted() {
		// Nested under Billables (perm 24); item actions use Billable Items (perm 26)
		this.$security.hasRequiredPermission('detail', 26).then(res => { this.permissions.detail = res })
		this.$security.hasRequiredPermission('add', 26).then(res => { this.permissions.add = res })
		this.$security.hasRequiredPermission('full_access', 26).then(res => { this.permissions.actions = res })
	},
	methods: {
		shortStatusLabel(item) {
			const labels = {
				0: 'N/A',
				1: 'Draft',
				2: 'In Review',
				3: 'Pending',
				4: 'Partially',
				5: 'Invoiced',
				6: 'Canceled',
			}
			return labels[item.status] || item.statusDisplayName || 'N/A'
		},
		approve(item, approved) {
			this.$adminSurf('approveBillableItem', item, approved)
				.then(res => {
					Object.assign(item, res.data)
					item.canApprove = false
					this.$emit('refresh')
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Cannot approve billable item')
				})
		},
		deleteBillableItem(item) {
			this.$refs.deleteDialog.open(item.id)
				.then(res => {
					if (!res) return
					this.$http.delete(`api/billableItem/${item.id}`)
						.then(() => {
							window.getApp.success('Deleted successfully')
							this.$emit('refresh')
						})
						.catch(err => {
							console.log(err)
							window.getApp.error('Cannot delete billable item')
						})
				})
		},
		createInvoice(item) {
			this.$router.push(`/admin/invoice/new?fromBillableItem=${item.id}`)
		},
	},
}
</script>

<style lang="scss" scoped>
.billable-item-nest {
	padding: 8px 8px 12px 28px;
	background: #f4f8fb;
	border-top: 1px solid rgba(25, 118, 210, 0.12);
}

.billable-item-nest__empty {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 8px;
}

.billable-item-nest__empty-text {
	font-size: 0.85rem;
	color: var(--v-newDesignText-primary);
	opacity: 0.7;
}

.bi-row {
	display: grid;
	grid-template-columns:
		minmax(120px, 1.2fr)
		minmax(90px, 0.85fr)
		minmax(90px, 0.85fr)
		minmax(60px, 0.65fr)
		minmax(52px, 0.5fr)
		minmax(80px, 0.8fr)
		minmax(80px, 0.8fr)
		minmax(80px, 0.8fr)
		minmax(96px, 0.9fr)
		84px;
	gap: 6px;
	align-items: start;
	padding: 6px 8px;
	border-radius: 0;
	border: none;
	box-shadow: none;

	& + .bi-row {
		border-top: 1px solid rgba(25, 118, 210, 0.1);
	}

	&--clickable {
		cursor: pointer;

		&:hover {
			background: rgba(25, 118, 210, 0.04);
		}
	}
}

.bi-cell {
	min-width: 0;
}

.bi-label {
	display: none;
}

.bi-header {
	align-items: center;
	padding-top: 2px;
	padding-bottom: 6px;

	&__cell {
		font-size: 0.78rem;
		font-weight: 500;
		color: var(--v-newDesignText-primary);
		opacity: 0.65;
		white-space: nowrap;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__cell--actions {
		text-align: right;
	}
}

.bi-value {
	font-size: 0.85rem;
	color: var(--v-newDesignText-base);
	line-height: 1.25;
	word-break: break-word;
	min-height: 34.5px;
	display: flex;
	align-items: center;
}

.bi-cell--actions {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.bi-label {
		width: 84px;
		text-align: right;
	}

	.bi-value {
		justify-content: flex-end;
		width: 84px;
	}
}

.bi-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	width: 84px;
	min-width: 84px;
	min-height: 28px;
	margin-left: auto;
}

.bi-actions__slot {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	min-width: 28px;
	flex-shrink: 0;
}

.status-chip {
	justify-content: center;
	width: 88px;
	min-width: 88px;
	max-width: 88px;
	height: 24px;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0;
	text-transform: none;

	::v-deep .v-btn__content {
		opacity: 1 !important;
	}
}
</style>
